import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { RemunerationGridService } from './remuneration-grid.service';
import { PracticeScopeService } from '../../../../core/services/practice-scope.service';
import {
  GRID_NOTE_INDEXES,
  RemunerationGrid,
  RemunerationGridField,
  RemunerationGridRow,
  RemunerationGridSetField,
  RemunerationGridTableType,
  RemunerationGridTypeField,
} from './remuneration-grid.model';
import { PermissionService } from '../../../../core/services/permission.service';
import { UnsavedChangesService } from '../../../../core/services/unsaved-changes.service';
import { SharedEvaluationService } from '../../../../core/services/shared-evaluation.service';
import { Permission } from '../../../../core/enums/permission.enum';
import { SalaryIncreaseType } from '../../../../core/models/salary-increase-type';

const UNSAVED_KEY = 'remuneration-grid';
const CONFIRM_KEY = 'remuneration-grid';

const GENERAL_CATEGORY_ORDER = [
  'CJ1', 'CJ2', 'CJ3', 'CJ4', 'CD', 'CD2',
];

export interface GridTable {
  field: RemunerationGridField;
  typeField: RemunerationGridTypeField;
  amountSet: RemunerationGridSetField;
  percentSet: RemunerationGridSetField;
  /** Type of rows that have none (older API responses). */
  defaultType: SalaryIncreaseType;
  titleKey: string;
  typeLabelKey: string;
  testId: string;
}

@Component({
  selector: 'em-remuneration-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    DropdownModule,
    InputNumberModule,
    InputSwitchModule,
    TableModule,
    ToastModule,
    TranslateModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './remuneration-grid.component.html',
  styleUrl: './remuneration-grid.component.scss',
})
export class RemunerationGridComponent implements OnInit, OnDestroy {
  readonly noteIndexes = GRID_NOTE_INDEXES;
  readonly confirmKey = CONFIRM_KEY;
  readonly tables: GridTable[] = [
    {
      field: 'bonusAmounts', typeField: 'bonusType', amountSet: 'bonusFixedAmounts', percentSet: 'bonusPercentages',
      defaultType: 'PERCENTAGE', titleKey: 'remunerationGrid.bonusTitle', typeLabelKey: 'remunerationGrid.bonusTypeLabel',
      testId: 'bonus-type',
    },
    {
      field: 'increasePercents', typeField: 'increaseType', amountSet: 'increaseAmounts', percentSet: 'increasePercentages',
      defaultType: 'AMOUNT', titleKey: 'remunerationGrid.increaseTitle', typeLabelKey: 'remunerationGrid.increaseTypeLabel',
      testId: 'increase-type',
    },
  ];
  readonly canWrite: boolean;
  /** True for a Manager / Manager People: they work on their own practice only. */
  readonly isPracticeScoped: boolean;

  practices: string[] = [];
  /** True once the practices were loaded, so "nothing selected" is not shown after a failed load. */
  practicesLoaded = false;
  years: number[] = [];
  selectedYear: number | null = null;
  selectedPractice: string | null = null;
  /** Last loaded or saved grid: the "saved snapshot". */
  grid: RemunerationGrid | null = null;
  /** Working copy edited in the tables. */
  rows: RemunerationGridRow[] = [];
  loading = true;
  saving = false;

  private loadedYear: number | null = null;
  private loadedPractice: string | null = null;
  private selectionChanged?: Subscription;

  constructor(
    private gridService: RemunerationGridService,
    private permissionService: PermissionService,
    private unsavedChangesService: UnsavedChangesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private sharedEvaluationService: SharedEvaluationService,
    private practiceScope: PracticeScopeService,
  ) {
    this.canWrite = this.permissionService.hasAnyPermission([Permission.MANAGER_WRITE, Permission.DIRECTOR_WRITE]);
    this.isPracticeScoped = this.practiceScope.isPracticeScopedUser();
  }

  ngOnInit(): void {
    this.unsavedChangesService.register(UNSAVED_KEY, () => this.isDirty);
    // The grid only shows categories selected in the Category tab, so follow that selection.
    this.selectionChanged = this.sharedEvaluationService.reloadData.subscribe(() => this.refreshPractices());
    forkJoin({ practices: this.gridService.getPractices(), years: this.gridService.getYears() }).subscribe({
      next: ({ practices, years }) => {
        this.practices = this.restrictPractices(practices);
        this.practicesLoaded = true;
        this.years = years;
        const currentYear = new Date().getFullYear();
        this.selectedYear = years.includes(currentYear) ? currentYear : (years[0] ?? currentYear);
        this.selectedPractice = this.practices[0] ?? null;
        if (this.selectedPractice === null) {
          this.loading = false;
        }
        this.load();
      },
      error: () => {
        this.loading = false;
        this.showError('remunerationGrid.messages.loadError');
      },
    });
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregister(UNSAVED_KEY);
    this.selectionChanged?.unsubscribe();
  }

  /**
   * Reloads the practices after the Category tab selection changed, then the grid.
   * Unsaved edits of a practice that is still listed are kept rather than thrown away.
   */
  refreshPractices(): void {
    this.gridService.getPractices().subscribe({
      next: (practices) => {
        practices = this.restrictPractices(practices);
        this.practices = practices;
        this.practicesLoaded = true;
        const stillListed = this.selectedPractice !== null && practices.includes(this.selectedPractice);
        if (stillListed && this.isDirty) {
          return;
        }
        if (!stillListed) {
          this.selectedPractice = practices[0] ?? null;
        }
        if (this.selectedPractice === null) {
          this.grid = null;
          this.rows = [];
          this.loadedPractice = null;
          return;
        }
        this.load();
      },
      error: () => this.showError('remunerationGrid.messages.loadError'),
    });
  }

  /**
   * A Manager / Manager People only gets their own practice, so the picker (hidden
   * for them) and the grid can never show another practice's rows.
   */
  private restrictPractices(practices: string[]): string[] {
    return this.practiceScope.restrict(practices ?? [], (practice) => practice);
  }

  get isDirty(): boolean {
    return !!this.grid && JSON.stringify(this.values(this.rows)) !== JSON.stringify(this.values(this.grid.rows));
  }

  /** Save is possible when something changed, or when the year + practice has not been saved yet (e.g. a copied year). */
  get canSave(): boolean {
    return this.canWrite && !!this.grid && !this.loading && !this.saving
      && !this.hasInvalidPercentageValues && (this.isDirty || !this.grid.saved);
  }

  /** Cells can be edited only by WRITE users and only when nothing is loading or saving. */
  get canEdit(): boolean {
    return this.canWrite && !this.loading && !this.saving;
  }

  isChanged(rowIndex: number, field: RemunerationGridField, noteIndex: number): boolean {
    const savedRow = this.grid?.rows[rowIndex];
    const row = this.rows[rowIndex];
    if (!savedRow || !row) {
      return false;
    }
    const table = this.tableOf(field);
    const savedValues = savedRow[this.setOf(row, table)] ?? [];
    return Number(savedValues[noteIndex]) !== Number(row[field][noteIndex]);
  }

  isTypeChanged(rowIndex: number, table: GridTable): boolean {
    const savedRow = this.grid?.rows[rowIndex];
    const row = this.rows[rowIndex];
    return !!savedRow && !!row && savedRow[table.typeField] !== row[table.typeField];
  }

  onCellChange(row: RemunerationGridRow, field: RemunerationGridField, noteIndex: number, value: number | null): void {
    row[field][noteIndex] = value ?? 0;
    const stored = row[this.setOf(row, this.tableOf(field))];
    if (stored) {
      stored[noteIndex] = value ?? 0;
    }
  }

  get increaseType(): RemunerationGridTableType {
    return this.tableType(this.tables[1]);
  }

  get increaseAsPercentage(): boolean {
    return this.increaseType === 'PERCENTAGE';
  }

  get hasInvalidPercentageValues(): boolean {
    const invalid = (value: number) => !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 100;
    return this.rows.some((row) => (row.increasePercentages ?? []).some(invalid) || (row.bonusPercentages ?? []).some(invalid));
  }

  tableType(table: GridTable): RemunerationGridTableType {
    const types = new Set(this.rows.map((row) => row[table.typeField] ?? table.defaultType));
    if (types.size === 0) {
      return table.defaultType;
    }
    return types.size === 1 ? [...types][0] : 'MIXED';
  }

  setTableType(table: GridTable, asPercentage: boolean): void {
    this.rows.forEach((row) => this.setRowType(row, table, asPercentage));
  }

  setRowType(row: RemunerationGridRow, table: GridTable, asPercentage: boolean): void {
    row[table.typeField] = asPercentage ? 'PERCENTAGE' : 'AMOUNT';
    row[table.field] = [...(row[this.setOf(row, table)] ?? this.zeroValues())];
  }

  onIncreaseTypeChange(asPercentage: boolean): void {
    this.setTableType(this.tables[1], asPercentage);
  }

  isPercentRow(row: RemunerationGridRow, table: GridTable): boolean {
    return (row[table.typeField] ?? table.defaultType) === 'PERCENTAGE';
  }

  cellMax(row: RemunerationGridRow, table: GridTable): number {
    return this.isPercentRow(row, table) ? 100 : 9999999999.99;
  }

  cellSuffix(row: RemunerationGridRow, table: GridTable): string {
    return this.isPercentRow(row, table) ? '%' : ' DH';
  }

  onYearChange(): void {
    this.confirmSelectionChange(() => (this.selectedYear = this.loadedYear));
  }

  onPracticeChange(): void {
    this.confirmSelectionChange(() => (this.selectedPractice = this.loadedPractice));
  }

  load(): void {
    if (this.selectedYear === null || this.selectedPractice === null) {
      return;
    }
    this.loading = true;
    this.gridService
      .getGrid(this.selectedYear, this.selectedPractice)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (grid) => this.applyGrid(grid),
        error: () => {
          if (this.grid) {
            this.selectedYear = this.loadedYear;
            this.selectedPractice = this.loadedPractice;
          }
          this.showError('remunerationGrid.messages.loadError');
        },
      });
  }

  save(): void {
    if (!this.grid || !this.canSave) {
      return;
    }
    this.saving = true;
    const rows = this.rows.map((row) => ({
      categoryShortLabel: row.categoryShortLabel,
      bonusAmounts: [...row.bonusAmounts],
      increasePercents: [...row.increasePercents],
      increaseAmounts: [...row.increaseAmounts],
      increasePercentages: [...row.increasePercentages],
      increaseType: row.increaseType ?? 'AMOUNT',
      bonusType: row.bonusType ?? 'PERCENTAGE',
      bonusFixedAmounts: [...(row.bonusFixedAmounts ?? this.zeroValues())],
      bonusPercentages: [...(row.bonusPercentages ?? this.zeroValues())],
    }));
    const defaultIncreaseType: SalaryIncreaseType = this.increaseType === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT';
    this.gridService
      .saveGrid(this.grid.year, this.grid.practice, defaultIncreaseType, rows)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (grid) => {
          this.applyGrid(grid);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('remunerationGrid.messages.successTitle'),
            detail: this.translate.instant('remunerationGrid.messages.saved'),
          });
          this.gridService.getYears().subscribe({ next: (years) => (this.years = years) });
        },
        error: () => this.showError('remunerationGrid.messages.saveError'),
      });
  }

  cancelChanges(): void {
    if (this.grid) {
      this.rows = this.copyRows(this.grid.rows);
    }
  }

  private confirmSelectionChange(revert: () => void): void {
    if (!this.isDirty) {
      this.load();
      return;
    }
    this.confirmationService.confirm({
      key: CONFIRM_KEY,
      header: this.translate.instant('worklog.unsavedChangesHeader'),
      message: this.translate.instant('worklog.unsavedChangesMessage'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('worklog.leave'),
      rejectLabel: this.translate.instant('worklog.stay'),
      accept: () => {
        this.cancelChanges();
        this.load();
      },
      reject: () => revert(),
    });
  }

  private applyGrid(grid: RemunerationGrid): void {
    const rows = grid.rows
      .map((row) => ({
        ...row,
        increaseType: row.increaseType ?? grid.increaseType ?? 'AMOUNT',
        bonusType: row.bonusType ?? 'PERCENTAGE',
      }))
      .sort((a, b) => {
        const aGeneral = GENERAL_CATEGORY_ORDER.indexOf(
          a.categoryShortLabel.trim().toUpperCase()
        );
        const bGeneral = GENERAL_CATEGORY_ORDER.indexOf(
          b.categoryShortLabel.trim().toUpperCase()
        );

        if (aGeneral !== -1 && bGeneral !== -1) {
          return aGeneral - bGeneral;
        }

        if (aGeneral !== -1) return -1;
        if (bGeneral !== -1) return 1;

        return a.level - b.level;
      });

    this.grid = { ...grid, rows: this.copyRows(rows) };
    this.rows = this.copyRows(rows);
    this.loadedYear = grid.year;
    this.loadedPractice = grid.practice;
  }

  private copyRows(rows: RemunerationGridRow[]): RemunerationGridRow[] {
    return rows.map((row) => {
      const increaseType = row.increaseType ?? 'AMOUNT';
      const bonusType = row.bonusType ?? 'PERCENTAGE';
      const increaseAmounts = (row.increaseAmounts
        ?? (increaseType === 'AMOUNT' ? row.increasePercents : this.zeroValues())).map(Number);
      const increasePercentages = (row.increasePercentages
        ?? (increaseType === 'PERCENTAGE' ? row.increasePercents : this.zeroValues())).map(Number);
      const bonusFixedAmounts = (row.bonusFixedAmounts
        ?? (bonusType === 'AMOUNT' ? row.bonusAmounts : this.zeroValues())).map(Number);
      const bonusPercentages = (row.bonusPercentages
        ?? (bonusType === 'PERCENTAGE' ? row.bonusAmounts : this.zeroValues())).map(Number);
      return {
        ...row,
        increaseType,
        bonusType,
        increaseAmounts,
        increasePercentages,
        bonusFixedAmounts,
        bonusPercentages,
        increasePercents: [...(increaseType === 'PERCENTAGE' ? increasePercentages : increaseAmounts)],
        bonusAmounts: [...(bonusType === 'AMOUNT' ? bonusFixedAmounts : bonusPercentages)],
      };
    });
  }

  private tableOf(field: RemunerationGridField): GridTable {
    return this.tables.find((table) => table.field === field)!;
  }

  private setOf(row: RemunerationGridRow, table: GridTable): RemunerationGridSetField {
    return this.isPercentRow(row, table) ? table.percentSet : table.amountSet;
  }

  private values(rows: RemunerationGridRow[]): unknown[] {
    return rows.map((row) => [
      row.categoryShortLabel,
      row.bonusType ?? 'PERCENTAGE',
      row.increaseType ?? 'AMOUNT',
      (row.bonusFixedAmounts ?? []).map(Number),
      (row.bonusPercentages ?? []).map(Number),
      row.increaseAmounts.map(Number),
      row.increasePercentages.map(Number),
    ]);
  }

  private zeroValues(): number[] {
    return this.noteIndexes.map(() => 0);
  }

  private showError(detailKey: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('remunerationGrid.messages.errorTitle'),
      detail: this.translate.instant(detailKey),
    });
  }
}
